
import React, { useState, useEffect, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import ImpersonationBanner from '../ImpersonationBanner';
import NewNotificationsPopup from '../NewNotificationsPopup';
import UnderTargetLoginPopup from '../UnderTargetLoginPopup';
import LockScreen from '../LockScreen';
import { useAuthStore } from '../../stores/auth.store';
import { usePermessiStore } from '../../stores/permessi.store';
import { useToast } from '../../lib/toast';

const WebRTCPhone = React.lazy(() => import('../WebRTCPhone'));

export default function AppShell() {
  // The lock overlay renders INSIDE the shell, never in place of it: the
  // whole point is that the tree — and with it the softphone's SIP
  // registration — stays mounted while the data is hidden.
  const locked = useAuthStore((s) => s.locked);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // I permessi di accesso si leggono una volta per sessione, qui: è il primo
  // punto in cui esiste un utente collegato e c'è una schermata da disegnare.
  const caricaPermessi = usePermessiStore((s) => s.carica);
  const statoPermessi = usePermessiStore((s) => s.stato);
  useEffect(() => {
    if (statoPermessi === 'iniziale') caricaPermessi();
  }, [statoPermessi, caricaPermessi]);

  // L'impersonazione dura 5 minuti e non si rinnova (vedi api.js): quando
  // finisce, la fascia rossa sparisce e i dati tornano a essere i propri. Una
  // schermata che cambia da sola senza dire perché sembra un guasto.
  const toast = useToast();
  useEffect(() => {
    const onScaduta = () => {
      toast.info('L\'impersonazione è scaduta dopo 5 minuti: sei tornato al tuo profilo.');
    };
    window.addEventListener('wfm-impersonation-expired', onScaduta);
    return () => window.removeEventListener('wfm-impersonation-expired', onScaduta);
  }, [toast]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className="wfm-app-shell relative flex h-full w-full overflow-hidden bg-bg-light"
      style={{ backgroundColor: 'var(--wfm-bg-light)', color: 'var(--wfm-text-main)' }}
    >
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex flex-col
          transition-all duration-300 ease-in-out
          lg:static lg:z-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'w-[72px]' : 'w-64'}
        `}
        style={{
          backgroundColor: 'var(--wfm-sidebar-bg)',
          borderRight: '1px solid var(--wfm-sidebar-border)',
          ...(sidebarCollapsed ? {} : { width: 'calc(16rem + env(safe-area-inset-left, 0px))' }),
        }}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </aside>

      {/* Main */}
      <div className="wfm-shell-main-col relative flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden">
        <ImpersonationBanner />
        <TopBar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        {/* `relative` makes <main> the containing block of every absolutely
            positioned node in a page (e.g. `.sr-only` labels): without it they
            escape this scroll area and stretch the window past the shell (#71). */}
        <main className="wfm-main-scroll relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      {/* Below the phone's z-50 on purpose — see LockScreen. */}
      {locked && <LockScreen />}
      <Suspense fallback={null}>
        <WebRTCPhone />
      </Suspense>
      <NewNotificationsPopup />
      <UnderTargetLoginPopup />
    </div>
  );
}
